// Library imports
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// User Component imports
import { PageContent, PageHeader } from 'components/common/layout_element/LayoutElement';
import { Row, Stack, LoadingBox } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import { setLoading, setErrorModal } from "_core/slices/scene/SceneSlice";
import AdminOpsAppearanceListApi from "apis/AdminOpsAppearanceListApi";
import AdminOpsAppearanceAddApi from "apis/AdminOpsAppearanceAddApi";
import AdminOpsAppearanceUpdateApi from "apis/AdminOpsAppearanceUpdateApi";
import AdminOpsAppearanceAssetUploadApi from "apis/AdminOpsAppearanceAssetUploadApi";
import AdminOpsAppearanceDeleteApi from "apis/AdminOpsAppearanceDeleteApi";
import AdminOpsVoicePreviewApi from "apis/AdminOpsVoicePreviewApi";
import { useOperatorPage } from "./common/useOperatorPage";
import AppearanceCard from "./appearance/AppearanceCard";
import AppearanceAddModal from "./appearance/AppearanceAddModal";
import AppearanceEditModal from "./appearance/AppearanceEditModal";
import VoicePreviewModal from "./appearance/VoicePreviewModal";
import { VIDEO_SLOTS } from "./appearance/appearanceDefs";
import { downloadAppearanceVideos } from "./appearance/videoArchive";

import './OpsCommon.scss';
import './AdminOpsAppearance.scss';

// 任意の動画は追加後に1本ずつ送る（idle / talk 以外）
const OPTIONAL_KEYS = VIDEO_SLOTS.filter((s) => !s.required).map((s) => s.key);
// 編集で差し替えを送る順
const UPLOAD_ORDER = VIDEO_SLOTS.map((s) => s.key);

const AdminOpsAppearance = () => {
    const dispatch = useDispatch();
    const ExecList = AdminOpsAppearanceListApi();
    const ExecAdd = AdminOpsAppearanceAddApi();
    const ExecUpdate = AdminOpsAppearanceUpdateApi();
    const ExecAssetUpload = AdminOpsAppearanceAssetUploadApi();
    const ExecDelete = AdminOpsAppearanceDeleteApi();
    const ExecVoicePreview = AdminOpsVoicePreviewApi();

    const [payload, setPayload] = useState(null);
    const [isListLoading, setIsListLoading] = useState(true);
    const [openMap, setOpenMap] = useState({});
    const [addOpen, setAddOpen] = useState(false);
    const [addKey, setAddKey] = useState(0);
    const [editId, setEditId] = useState(null);
    const [progress, setProgress] = useState(null);
    const [previewVoiceId, setPreviewVoiceId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const showError = (response) => {
        dispatch(setErrorModal({ show: true, text: response?.error_detail ?? { message: Constants.UNKNOWN_ERROR_DETAIL } }));
    };

    const LoadList = async () => {
        setIsListLoading(true);
        const { response } = await ExecList();
        setIsListLoading(false);
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setPayload(response);
    };

    const { canUse } = useOperatorPage(LoadList);

    // ドロップ先から外れた場所へ落としたファイルをブラウザが開かないようにする
    useEffect(() => {
        if (!addOpen && editId === null) return undefined;
        const prevent = (e) => e.preventDefault();
        window.addEventListener("dragover", prevent);
        window.addEventListener("drop", prevent);
        return () => {
            window.removeEventListener("dragover", prevent);
            window.removeEventListener("drop", prevent);
        };
    }, [addOpen, editId]);

    const appearances = payload?.appearance_list ?? [];
    const voices = payload?.voice_list ?? [];
    const maxFileSize = payload?.max_file_size ?? Constants.MAX_UPLOAD_FILE_SIZE;

    // 初期状態で開くのは適用中の見た目だけ
    const isOpen = (ap) => (openMap[ap.id] !== undefined ? openMap[ap.id] : ap.is_use);
    const toggleOpen = (ap) => {
        const next = !isOpen(ap);
        setOpenMap((prev) => ({ ...prev, [ap.id]: next }));
    };

    const applyPayload = (response) => {
        const { t_appearance_list, t_asset_list, t_user_add, ...rest } = response;
        setPayload(rest);
    };

    // 動画を1本ずつ送る（進捗は全体のサイズに対する割合）
    const uploadAssets = async (tAppearanceId, entries, doneBytes, totalBytes) => {
        let done = doneBytes;
        for (const [kind, entry] of entries) {
            const offset = done;
            const { response } = await ExecAssetUpload(tAppearanceId, kind, entry.file, (ratio) => {
                setProgress(Math.min(0.99, (offset + entry.size * ratio) / totalBytes));
            });
            if (!response || response.error_code !== 0) return { ok: false, response };
            applyPayload(response);
            done += entry.size;
        }
        return { ok: true };
    };

    const addAppearance = async ({ name, voiceId, files }) => {
        const optional = OPTIONAL_KEYS.filter((k) => files[k]).map((k) => [k, files[k]]);
        const totalBytes = files.idle.size + files.talk.size + optional.reduce((sum, [, f]) => sum + f.size, 0);
        setProgress(0);
        const { response } = await ExecAdd({ name, tVoiceTypeId: voiceId, idleFile: files.idle.file, talkFile: files.talk.file },
            (ratio) => setProgress(Math.min(0.99, ((files.idle.size + files.talk.size) * ratio) / totalBytes)));
        if (!response || response.error_code !== 0) {
            setProgress(null);
            showError(response);
            return;
        }
        applyPayload(response);
        const newId = response.t_appearance_id;
        const result = await uploadAssets(newId, optional, files.idle.size + files.talk.size, totalBytes);
        setProgress(null);
        setAddOpen(false);
        setOpenMap((prev) => ({ ...prev, [newId]: true }));
        if (!result.ok) {
            // 外見は作成済み。残りの動画は編集から追加し直せるよう、編集を開いてエラーを出す
            setEditId(newId);
            showError(result.response);
        }
    };

    const saveAppearance = async (appearance, { name, voiceId, files }) => {
        const entries = UPLOAD_ORDER.filter((k) => files[k]).map((k) => [k, files[k]]);
        const totalBytes = Math.max(1, entries.reduce((sum, [, f]) => sum + f.size, 0));
        setProgress(0);
        if (name !== appearance.name || voiceId !== appearance.t_voice_type_id) {
            const { response } = await ExecUpdate(appearance.id, name, voiceId);
            if (!response || response.error_code !== 0) {
                setProgress(null);
                showError(response);
                return;
            }
            applyPayload(response);
        }
        const result = await uploadAssets(appearance.id, entries, 0, totalBytes);
        setProgress(null);
        if (!result.ok) {
            showError(result.response);
            return;
        }
        setEditId(null);
    };

    const deleteAppearance = async (appearance) => {
        dispatch(setLoading(true));
        const { response } = await ExecDelete(appearance.id);
        dispatch(setLoading(false));
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        applyPayload(response);
        setEditId(null);
    };

    const downloadVideos = async (appearance) => {
        setDownloadingId(appearance.id);
        try {
            await downloadAppearanceVideos(appearance);
        } catch (e) {
            showError({ error_detail: { message: "動画のダウンロードに失敗しました。時間をおいてもう一度お試しください。" } });
        } finally {
            setDownloadingId(null);
        }
    };

    const synthesize = async (voiceId, text, style) => {
        const { response } = await ExecVoicePreview(voiceId, text, style);
        if (!response || response.error_code !== 0) {
            showError(response);
            return null;
        }
        return response.audio_data;
    };

    const openAdd = () => {
        setAddKey((k) => k + 1);
        setAddOpen(true);
    };

    const editing = appearances.find((ap) => ap.id === editId);
    const previewVoice = voices.find((v) => v.id === previewVoiceId);

    return (
        <PageContent pageId="AdminOpsAppearance">
            <div className="ops-scope width-full">
                <PageHeader>
                    <Row className="flex-both align-center width-full">
                        <Stack className="spacing-4px">
                            <div className="page-header-title">外見動画管理</div>
                            <div className="page-header-detail">AIの見た目とボイスを管理します。外見の適用は表示設定ページより行ってください。</div>
                        </Stack>
                    </Row>
                </PageHeader>

                <div className="ops-appearance-body">
                    <div className="ops-appearance-toolbar">
                        <div className="ops-appearance-count">{appearances.length} 件の外見動画</div>
                        <div className="ops-appearance-toolbar-actions">
                            <button type="button" className="ops-pill" onClick={LoadList} disabled={isListLoading}><i className="bi bi-arrow-clockwise" />再取得</button>
                            <button type="button" className="ops-pill is-primary" onClick={openAdd} disabled={!payload} title="外見動画の追加フォームを開きます">
                                <i className="bi bi-plus" />外見動画を追加
                            </button>
                        </div>
                    </div>

                    {isListLoading && !payload ?
                        <div className="ops-appearance-loading">{canUse && <LoadingBox />}</div>
                        :
                        appearances.map((ap) => (
                            <AppearanceCard key={ap.id} appearance={ap} isOpen={isOpen(ap)}
                                onToggle={() => toggleOpen(ap)} onEdit={() => setEditId(ap.id)}
                                onDownload={() => downloadVideos(ap)} isDownloading={downloadingId === ap.id} />
                        ))
                    }
                    {payload && !appearances.length && <div className="ops-appearance-empty">外見動画が登録されていません。</div>}
                </div>

                {addOpen &&
                    <AppearanceAddModal key={addKey} show={addOpen} voices={voices} maxFileSize={maxFileSize} progress={progress}
                        onClose={() => setAddOpen(false)} onSubmit={addAppearance} onPreviewVoice={setPreviewVoiceId} />
                }
                {editing &&
                    <AppearanceEditModal key={editing.id} appearance={editing} voices={voices} maxFileSize={maxFileSize} progress={progress}
                        onClose={() => setEditId(null)} onSave={(values) => saveAppearance(editing, values)}
                        onDelete={() => deleteAppearance(editing)} onPreviewVoice={setPreviewVoiceId} />
                }
                {previewVoice &&
                    <VoicePreviewModal key={previewVoice.id} voice={previewVoice} onClose={() => setPreviewVoiceId(null)} onSynthesize={synthesize} />
                }
            </div>
        </PageContent>
    );
};

export default AdminOpsAppearance;
